import React, { useRef, useState } from 'react';
import { Grid, Theme, useMediaQuery } from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'i18n';
import { useEmbeddable, useQueryParams } from 'hooks';
import { useDispatch } from 'react-redux';
import { routes } from 'utils/routing';
import { endOfMonth, startOfMonth } from 'date-fns';
import { getCalendarDateFilter } from 'components/filters/dateFilters';
import { useCalendarEvents, useCalendarHandlers, useEventPopover } from 'hooks/calendarHooks';
import { renderEventContent } from 'components/CalendarComponents/renderEventContent';
import CalendarHeader from 'components/CalendarComponents/CalendarHeader';
import CalendarWeekHeader from 'components/CalendarComponents/CalendarWeekHeader';
import EventsDialog from 'components/CalendarComponents/EventsDialog';
import EventInfoView from './EventInfoView';
import MobileEventsCalendarView from './MobileEventsCalendarView';

export interface IProps {
  top?: React.ReactNode;
  events: any[];
  filtersHeight?: number;
}

const useStyles = makeStyles()((theme) => ({
  root: {
    '&&': {
      '.fc-daygrid-day-top': {
        flexDirection: 'row',
        paddingLeft: 8,
        fontFamily: 'Poppins',
        fontSize: '18px',
        fontStyle: 'normal',
        fontWeight: 500,
      },
      '.fc-day:hover:not(:has(.fc-daygrid-event:hover))': {
        backgroundColor: '#F5F6FA',
      },
      '.fc-day-today': {
        backgroundColor: 'transparent',
        boxShadow: 'inset 0 3px 0 0 #4E6DF5',
      },
      '.fc-daygrid-dot-event': {
        marginLeft: 0,
        marginRight: 0,
        borderRadius: 4,
        paddingLeft: 2,
      },
      '.fc-daygrid-dot-event:hover': {
        backgroundColor: 'rgba(209, 223, 255, 0.3)',
      },
      '.fc-h-event': {
        backgroundColor: theme.palette.primary.light,
        border: `1px solid ${theme.palette.primary.light}`,
      },
      '.fc-h-event.fc-event-draft': {
        backgroundColor: 'rgba(226, 217, 250, 0.4)',
        border: '1px solid rgba(226, 217, 250, 0.4)',
        color: theme.palette.text.primary,
      },
      '.fc-h-event.fc-event-draft .fc-event-main': {
        color: theme.palette.text.primary,
      },
      '.fc-h-event.fc-event-draft:hover': {
        backgroundColor: (theme.palette as any).custom.purple.extraLight,
        borderColor: (theme.palette as any).custom.purple.extraLight,
      },
      '.fc-daygrid-dot-event.fc-event-draft': {
        backgroundColor: 'rgba(226, 217, 250, 0.4)',
        borderRadius: '4px',
        marginLeft: 0,
        marginRight: 0,
        paddingLeft: 2,
      },
      '.fc-daygrid-dot-event.fc-event-draft:hover': {
        backgroundColor: (theme.palette as any).custom.purple.extraLight,
      },
      '.fc-daygrid-day-bottom': {
        display: 'flex',
        justifyContent: 'end',
        color: theme.palette.primary.main,
        fontFamily: 'Poppins',
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'uppercase',
      },
    },
  },
  dayHeader: {
    '&&': {
      textAlign: 'start',
      color: '#7B8BAD',
      fontFamily: 'Poppins',
      fontSize: '11px',
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: '13.2px',
    },
  },
}));

const EventsCalendarView: React.FC<React.PropsWithChildren<IProps>> = (props) => {
  const { top, events, filtersHeight } = props;
  const { classes } = useStyles();
  const [t] = useTranslation('public');
  const [queryParams, setQueryParams] = useQueryParams();
  const isEmbedded = useEmbeddable();
  const calendarRef = useRef<FullCalendar | null>(null);
  const dispatch = useDispatch();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'), { noSsr: true });

  const [currentDate, setCurrentDate] = useState(new Date());
  const [moreEvents, setMoreEvents] = useState<any[]>([]);
  const [moreDate, setMoreDate] = useState<Date | null>(null);
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  const { selectedEvent, anchorPosition, handleEventClick, handleClosePopover } = useEventPopover(events, dispatch);
  const calendarEvents = useCalendarEvents(events, currentDate);
  const handleToday = () => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    api.today();
    const today = new Date();
    const normalizedToday = new Date(today.getFullYear(), today.getMonth(), 1);
    setCurrentDate(normalizedToday);
    setQueryParams({
      ...queryParams,
      ...getCalendarDateFilter(startOfMonth(normalizedToday), endOfMonth(normalizedToday)),
    });
  };

  const { handlePrevMonth, handleNextMonth, handleDateClick, handleMoreLinkClick } = useCalendarHandlers({
    calendarRef,
    setCurrentDate,
    queryParams,
    setQueryParams,
    events,
    calendarEvents,
    setMoreEvents,
    setMoreDate,
    setIsMoreOpen,
    handleClosePopover,
  });

  const getEventLink = (eventId: number) => routes.event(eventId).link;

  const handleGetTicketsClick = (eventId: number) => {
    const link = getEventLink(eventId);
    // In embed mode, navigating the iframe itself would take the visitor away from the partner
    // site — open the event in a new tab instead (JPA-8032).
    if (isEmbedded) {
      window.open(link, '_blank', 'noopener');
      return;
    }
    window.location.href = link;
  };

  const currentMonthLabel = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
  }).format(currentDate);

  // Wrap renderEventContent to pass translation function
  const eventContentRenderer = (eventInfo: any) => renderEventContent(eventInfo, t);

  const eventClassNamesHandler = (arg: any) => {
    const { isDraft } = arg.event.extendedProps;
    return isDraft ? ['fc-event-draft'] : [];
  };

  if (isMobile) return <MobileEventsCalendarView top={top} events={events} filtersHeight={filtersHeight} />;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {top}
      </Grid>

      <Grid item xs={12}>
        <CalendarHeader
          currentMonthLabel={currentMonthLabel}
          onPrevMonth={handlePrevMonth}
          onNextMonth={handleNextMonth}
          onTodayClick={handleToday}
        />
      </Grid>

      <Grid item xs={12}>
        <CalendarWeekHeader />
      </Grid>

      <Grid item xs={12} style={{ paddingTop: '2px' }}>
        <FullCalendar
          ref={calendarRef}
          timeZone="local"
          viewClassNames={[classes.root]}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={false}
          dayHeaderClassNames={[classes.dayHeader]}
          dayHeaders={false}
          events={calendarEvents}
          eventContent={eventContentRenderer}
          eventClassNames={eventClassNamesHandler}
          dayMaxEvents={4}
          eventClick={handleEventClick}
          dateClick={handleDateClick}
          moreLinkClick={handleMoreLinkClick}
          fixedWeekCount={false}
        />
      </Grid>

      <EventsDialog
        open={isMoreOpen}
        onClose={() => setIsMoreOpen(false)}
        moreDate={moreDate}
        moreEvents={moreEvents}
        onGetTicketsClick={handleGetTicketsClick}
        isMobile={isMobile}
      />

      <EventInfoView
        anchorPosition={anchorPosition}
        handleClosePopover={handleClosePopover}
        selectedEvent={selectedEvent}
      />
    </Grid>
  );
};

export default EventsCalendarView;
